// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  Card,
  CardContent,
  Fab,
  Fade,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { RootState, useAppSelector } from "../../slices/store";
import { LOCAL_STORAGE_KEYS, BANNER_TOGGLE_EVENT } from "../../config/constant";
import { BannerToggleDetail } from "../../utils/types";

type NotificationBannerCardProps = {
  initialOpen?: boolean;
};

const NotificationBannerCard = ({ initialOpen }: NotificationBannerCardProps) => {
  const bannerId = LOCAL_STORAGE_KEYS.NOTIFICATION_BANNER_DISMISSED;

  const [open, setOpen] = useState<boolean>(
    initialOpen ?? true
  );
  const appMaintenanceStatus = useAppSelector(
    (state: RootState) => state.app.response
  );

  // Read initial visibility from localStorage on mount and respect persisted dismissals
  useEffect(() => {
    if (localStorage.getItem(bannerId) === "hide") {
      setOpen(false);
    }

    const onBannerToggle = (e: Event) => {
      const ev = e as CustomEvent<BannerToggleDetail>;
      if (ev?.detail?.notification !== undefined) {
        setOpen(!!ev.detail.notification);
      }
    };

    window.addEventListener(BANNER_TOGGLE_EVENT, onBannerToggle as EventListener);
    return () =>
      window.removeEventListener(
        BANNER_TOGGLE_EVENT,
        onBannerToggle as EventListener
      );
  }, [initialOpen, bannerId]);

  return (
    <>
      {appMaintenanceStatus.isShowMaintenanceBanner && appMaintenanceStatus.maintenanceBannerContent && (
        <>
          <Slide
            timeout={1000}
            direction="left"
            in={open}
            mountOnEnter
            unmountOnExit
          >
            <Card
              sx={{
                width: "60%",
                position: "fixed",
                top: 100,
                right: 20,
                zIndex: "1000",
                borderRadius: 5,
                py: 3,
                px: 1,
              }}
              elevation={3}
            >
              <Stack
                sx={{ mr: 1, mt: -2 }}
                direction="row"
                justifyContent="flex-end"
              >
                <IconButton
                  onClick={() => {
                    setOpen(false);
                    localStorage.setItem(bannerId, "hide");
                    window.dispatchEvent(
                      new CustomEvent<BannerToggleDetail>(BANNER_TOGGLE_EVENT, { detail: { notification: false } })
                    );
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
              <CardContent sx={{ marginTop: -2 }}>
                {appMaintenanceStatus.maintenanceBannerTitle && (
                  <Typography
                    sx={{ fontWeight: "bold" }}
                    variant="h5"
                    color="secondary.main"
                  >
                    {appMaintenanceStatus.maintenanceBannerTitle}
                  </Typography>
                )}

                <Typography variant="body2" mt={2}>
                  {appMaintenanceStatus.maintenanceBannerContent}
                </Typography>
              </CardContent>
            </Card>
          </Slide>
          {/* FAB rendering moved to LandingPage to avoid overlap with other banners */}
        </>
      )}
    </>
  );
};

export default NotificationBannerCard;
